import React, { useCallback, useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import SuggestionListing from './SuggestionListing';
import { ISuggestion, useSuggestions } from '../../context/SuggestionsContext';
import { NOTIFICATION_AREAS } from '../../constants/suggestions';

const PAGE_SIZE = 100;

interface CategoryCacheEntry { items: ISuggestion[]; total: number; }

interface SuggestionViewMorePanelProps {
  isOpen: boolean;
  onDismiss: () => void;
}

const SuggestionViewMorePanel: React.FC<SuggestionViewMorePanelProps> = ({ isOpen, onDismiss }) => {
  const { suggestions, suggestionsCount, getSuggestionsPage } = useSuggestions();
  const [pageData, setPageData] = useState<ISuggestion[]>([]);
  const [pageTotal, setPageTotal] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>({});
  const [categoryCache, setCategoryCache] = useState<Record<string, CategoryCacheEntry>>({});

  useEffect(() => {
    if (!isOpen) return;

    // Reset everything and seed "All" from context immediately — no spinner
    setSelectedCategory('All');
    setCategoryCounts({});
    setCategoryCache({});
    setSkip(0);
    setPageLoading(false);
    setPageData(suggestions);
    setPageTotal(suggestionsCount);

    // Fire all 13 category fetches independently so counts appear as they arrive
    NOTIFICATION_AREAS.forEach(async (area) => {
      try {
        const { suggestions: items, total } = await getSuggestionsPage(0, PAGE_SIZE, area);
        setCategoryCounts(prev => ({ ...prev, [area]: total }));
        setCategoryCache(prev => ({ ...prev, [area]: { items, total } }));
      } catch {
        setCategoryCounts(prev => ({ ...prev, [area]: 0 }));
      }
    });
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps
  // Intentionally exclude suggestions/getSuggestionsPage — only re-run when the panel toggles open

  const fetchPage = useCallback(async (newSkip: number, category: string) => {
    setPageLoading(true);
    const { suggestions: items, total } = await getSuggestionsPage(
      newSkip,
      PAGE_SIZE,
      category !== 'All' ? category : undefined
    );
    setPageData(items);
    setPageTotal(total);
    setSkip(newSkip);
    if (newSkip === 0) {
      setCategoryCache(prev => ({ ...prev, [category]: { items, total } }));
    }
    setPageLoading(false);
  }, [getSuggestionsPage]);

  const handleCategoryChange = useCallback((area: string) => {
    setSelectedCategory(area);
    setSkip(0);
    if (area === 'All') {
      setPageData(suggestions);
      setPageTotal(suggestionsCount);
      return;
    }
    const cached = categoryCache[area];
    if (cached) {
      setPageData(cached.items);
      setPageTotal(cached.total);
    } else {
      fetchPage(0, area);
    }
  }, [suggestions, suggestionsCount, categoryCache, fetchPage]);

  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;
  const totalPages = Math.ceil(pageTotal / PAGE_SIZE);

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onDismiss()}>
      <SheetContent side="right" className="flex flex-col w-full sm:w-[90vw]" style={{ maxWidth: 'none', top: '70px', height: 'calc(100vh - 70px)' }}>
        <SheetHeader>
          <SheetTitle>Notifications</SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto mt-4">
          <SuggestionListing
            items={pageData}
            totalCount={pageTotal}
            categoryCounts={categoryCounts}
            activeCategory={selectedCategory}
            showCategoriesOnListing={true}
            showDocuSignMessage={true}
            adminMode={false}
            loading={pageLoading}
            RefreshData={() => fetchPage(skip, selectedCategory)}
            onCategoryChange={handleCategoryChange}
          />
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-end gap-2 p-3 border-t border-border">
            <span className="text-sm text-muted-foreground mr-2">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={skip === 0 || pageLoading}
              onClick={() => fetchPage(skip - PAGE_SIZE, selectedCategory)}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages || pageLoading}
              onClick={() => fetchPage(skip + PAGE_SIZE, selectedCategory)}
            >
              Next
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default SuggestionViewMorePanel;
